/**
 * Builds a StowedElement from frontend ShapeData.
 *
 * Maps ShapeData properties to the proper protobuf-derived StowedElement
 * structure required by the backend API. The element data is placed
 * under the correct type-specific field based on kindId.
 */

import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { GROUP_KIND_ID } from '@/lib/groups/group-kind'
import { KIND_TO_FIELD, type StowedElement } from '@/api/types/stowed-element'
import { buildTextOnPathElement } from '@/lib/elements/text-on-path-element'
import { hasTextOnPathContent } from '@/lib/shapes/text-on-path-session'
import type { ParagraphBlockFontLookup } from '@/lib/text/types/paragraph-block-font-lookup'
import { fractionalIndexForStowedObject } from '@/lib/elements/stowed-fractional-index'
import { elementUsesVisibleEditorRect } from '@/lib/elements/read-page-element-editor-rect'
import type { DocumentProjection } from '@/lib/canvas/types/document-projection'
import { IDENTITY_PROJECTION } from '@/lib/canvas/document-projection'
import { SIDES_ELEMENT_KIND_ID, isSidesEligibleShapeType } from '@/lib/object-effects/sides'
import type { GeometricPersistOp } from '@/lib/elements/types/geometric-persist-op'
import { TransformPersistSource } from './types/transform-persist-source'
import { BuildStowedShapeOptions } from './types/build-stowed-shape-options'
import { isClosedPenPathShape } from './helpers/is-closed-pen-path-shape'
import { buildBase } from './helpers/build-base'
import { buildEditorShape } from './helpers/build-editor-shape'
import { buildTextWrap } from './helpers/build-text-wrap'
import {
  buildIrregularPolygonElement,
  buildOvalElement,
  buildPolylineElement,
  buildRectangleElement,
  buildRegularPolygonElement,
} from './helpers/build-kind-elements'

/** Maps a user transform gesture to the visible-rect op (resize is not `update`). */
export function visibleRectOpFromTransformSource(
  source: TransformPersistSource | undefined
): GeometricPersistOp | undefined {
  if (source === 'rotate') return 'rotate'
  if (source === 'resize') return 'resize'
  if (source === 'move' || source === 'align') return 'move'

  return undefined
}

/**
 * Whether to emit `visible*` on `base` for a geometric PUT.
 *
 * Rotated elements reload from `visible*`; move/resize must update it or position resets after refresh.
 * Pass `transformSource` when the persist operation is `update` but the gesture was move/resize.
 */
export function buildStowedVisibleRectOptions(
  rotation: number | undefined,
  persistOperation?: GeometricPersistOp,
  transformSource?: TransformPersistSource
): BuildStowedShapeOptions {
  const visibleOp =
    visibleRectOpFromTransformSource(transformSource) ??
    (persistOperation === 'rotate' || persistOperation === 'move' || persistOperation === 'resize'
      ? persistOperation
      : undefined)

  const rotated = elementUsesVisibleEditorRect(rotation ?? 0)
  const include =
    visibleOp === 'rotate' ||
    ((visibleOp === 'move' || visibleOp === 'resize') && rotated) ||
    // Canvas resize often persists as `update`; undo/redo uses the same. Keep `visible*` in sync.
    (persistOperation === 'update' && rotated)

  return { includeVisibleForRotate: include }
}

/**
 * The kindId a shape will resolve to on save, even before it has one of its own —
 * freshly created shapes (placed from the Shapes panel, not yet round-tripped through
 * a checkpoint save/load) leave `kindId` unset until this function assigns one.
 */
export function resolveKindId(shape: ShapeData): number {
  // A closed pen-path shape carrying a photo fill must always resolve to kindId 2, regardless of
  // any already-persisted kindId — legacy always promotes a closed hand-drawn loop to "Irregular
  // Polygon" (kindId 2, `<IP>`) the instant it's closed (see the comment below), and its own
  // `<PL>` (kindId 9) reader doesn't understand an image fill. Without this check, a shape that
  // started life as an open kindId-9 path and was later closed — e.g. via the "Clipping Path"
  // node-edit gesture — would keep saving as kindId 9 forever, since the persisted-kindId
  // early-return below would otherwise win: Larry's own renderer shows the photo fine either way
  // (it clips from `path` directly, not from wire kindId), but the photo silently disappears the
  // moment the same spread is opened in legacy.
  if (shape.type === 'path' && shape.photoFill && isClosedPenPathShape(shape)) {
    return 2
  }

  if (typeof shape.kindId === 'number' && Number.isFinite(shape.kindId)) {
    return shape.kindId
  }

  if (shape.type === 'path') {
    // Legacy re-types a hand-drawn pen path to "Irregular Polygon" the instant the user closes
    // the loop (clicks back on the start anchor) and always saves it as kindId 2 (`<IP>`), never
    // as a closed kindId 9 polyline (`<PL>`) — see ODEditPath.convertToSegmentsPath /
    // ODPath.prototype.toXML in odeditorsvg.js. Only a genuinely open pen path saves as kindId 9.
    return isClosedPenPathShape(shape) ? 2 : 9
  }

  if (shape.type === 'square') {
    return 1
  }

  if (isSidesEligibleShapeType(shape.type)) {
    return SIDES_ELEMENT_KIND_ID
  }

  return 2
}

/**
 * Build a complete StowedElement from frontend ShapeData.
 *
 * The element data is placed under the correct field name based on kindId,
 * conforming to the backend's StowedElement validation contract.
 *
 * Prefer {@link buildStowedVisibleRectOptions} for geometric PUTs. Rotated elements also need
 * `visible*` on move/resize so checkpoint reload matches the canvas.
 */
export function buildStowedElement(
  shape: ShapeData,
  options?: BuildStowedShapeOptions,
  projection: DocumentProjection = IDENTITY_PROJECTION,
  fonts?: ParagraphBlockFontLookup
): StowedElement {
  const kindId = resolveKindId(shape)
  const fieldName = KIND_TO_FIELD[kindId]

  const stowed: StowedElement = {
    objectId: shape.id,
    kindId,
    fractionalIndex: fractionalIndexForStowedObject(shape.id, shape.fractionalIndex),
    editorShape: buildEditorShape(shape, fonts),
  }

  switch (kindId) {
    case 1:
      stowed[fieldName as 'rectangleElement'] = buildRectangleElement(
        shape,
        options,
        projection,
        fonts
      )
      break
    case 2:
      stowed[fieldName as 'irregularPolygonElement'] = buildIrregularPolygonElement(
        shape,
        options,
        projection,
        fonts
      )
      break
    case 8:
      stowed[fieldName as 'ovalElement'] = buildOvalElement(shape, options, projection)
      break
    case 9:
      stowed[fieldName as 'polylineElement'] = buildPolylineElement(
        shape,
        options,
        projection,
        fonts
      )
      break
    case SIDES_ELEMENT_KIND_ID:
      stowed[fieldName as 'regularPolygonElement'] = buildRegularPolygonElement(
        shape,
        options,
        projection,
        fonts
      )
      break
    case GROUP_KIND_ID:
      // A group is treated as one wrapping unit — its own textWrap, not each child's.
      stowed[fieldName as 'groupElement'] = {
        base: buildBase(shape, options, projection),
        ...buildTextWrap(shape),
      }
      break
    default: {
      // For element types not yet fully supported in the editor.
      // place a minimal structure with base geometry.
      ;(stowed as unknown as Record<string, unknown>)[fieldName ?? 'rectangleElement'] = {
        base: buildBase(shape, options, projection),
      }
      break
    }
  }

  // Text on a Path (WMPCM-9542) rides the host's outline, so it is written ALONGSIDE the host's own
  // element field and the host keeps its own `kindId` — `<TP>` is a sibling of `<RR>`/`<IP>`/`<RP>`
  // inside one `<PE>`, not a page element of its own. Promoting the host to `kindId: 6` instead
  // would make the text the element the backend serialises (it dispatches on `kindId` alone, see
  // `GetUnderlyingElement`) and silently drop the shape the text runs along. See
  // `lib/elements/text-on-path-element.ts`.
  if (hasTextOnPathContent(shape.textOnPath)) {
    stowed.textOnPathElement = buildTextOnPathElement({
      config: shape.textOnPath,
      base: buildBase(shape, options, projection),
      characters: options?.textOnPathCharacters,
    })
  }

  return stowed
}

/**
 * True when a StowedElement row (as produced by `buildStowedElement`, including through
 * `buildGroupTransformPersistBatch`/other group builders) carries text — i.e. a `kindId: 1`
 * rectangle with a populated `content.text`. There is no separate "text box" wire kind to detect
 * (D2, text-unification): any shape can carry text, so this is a content check, not a
 * classification of the element's origin. Used where code needs to pick text-carrying rows back
 * out of a mixed `StowedElement[]` batch.
 */
export function isStowedTextElement(row: StowedElement): boolean {
  return row.kindId === 1 && row.rectangleElement?.content?.text != null
}
